import { useEffect, useRef, useState } from "react";
import { ExternalLink, Monitor, RefreshCw, Smartphone, Tablet, X } from "lucide-react";

const VIEWPORTS = [
  { id: "desktop", label: "Ordinateur", icon: Monitor, width: 1280, height: 1400 },
  { id: "tablet", label: "Tablette", icon: Tablet, width: 768, height: 1100 },
  { id: "mobile", label: "Mobile", icon: Smartphone, width: 390, height: 844 },
];

const CONTENT_SAVED_EVENT = "eudemonie:content-saved";
export const announceContentSaved = () => window.dispatchEvent(new Event(CONTENT_SAVED_EVENT));

export default function ContentPublicPreview({ children, url = "/", title = "Aperçu public", status = "published", dirty = false, focusHash = "", refreshKey = 0, action = null }) {
  const [viewportId,setViewportId]=useState("desktop");
  const [scale,setScale]=useState(.4);
  const [reload,setReload]=useState(0);
  const [refreshing,setRefreshing]=useState(false);
  const [mobileOpen,setMobileOpen]=useState(false);
  const hostRef=useRef(null);
  const iframeRef=useRef(null);
  const focusTimerRef=useRef(null);
  const viewport=VIEWPORTS.find(item=>item.id===viewportId)||VIEWPORTS[0];

  useEffect(()=>{
    const host=hostRef.current;
    if(!host)return undefined;
    const resize=()=>setScale(Math.min(host.clientWidth/viewport.width,1));
    resize();
    const observer=new ResizeObserver(resize);
    observer.observe(host);
    return()=>observer.disconnect();
  },[viewport.width,mobileOpen]);

  useEffect(()=>()=>clearTimeout(focusTimerRef.current),[]);
  useEffect(()=>{
    const refreshAfterSave=()=>{setRefreshing(true);setReload(value=>value+1)};
    window.addEventListener(CONTENT_SAVED_EVENT,refreshAfterSave);
    return()=>window.removeEventListener(CONTENT_SAVED_EVENT,refreshAfterSave);
  },[]);

  const focusSection=(attempt=0)=>{
    if(!focusHash)return;
    try{
      const target=iframeRef.current?.contentDocument?.querySelector(focusHash);
      if(target){target.scrollIntoView({block:"start"});return;}
    }catch{}
    if(attempt<8)focusTimerRef.current=setTimeout(()=>focusSection(attempt+1),300);
  };
  const onLoad=()=>{setRefreshing(false);focusSection()};
  const refresh=()=>{setRefreshing(true);setReload(value=>value+1)};
  const openMobile=()=>{setViewportId("mobile");setMobileOpen(true)};

  const preview=<aside className={`${mobileOpen?"fixed inset-0 z-[70] overflow-y-auto bg-bone p-4":"hidden"} xl:sticky xl:top-6 xl:block xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto xl:p-0`} aria-label={title}>
    <div className="overflow-hidden rounded-2xl border border-sage/15 bg-white shadow-sm">
      <div className="sticky top-0 z-10 space-y-3 border-b border-sage/10 bg-white p-3">
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-xs font-semibold uppercase tracking-[.16em] text-sage">{title}</p><span className={status==="published"?"mt-1 inline-flex rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-800":"mt-1 inline-flex rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-800"}>{status==="published"?"Publié sur le site":"Brouillon · absent du site public"}</span></div>
          <div className="flex items-center gap-2">{action&&<div className="xl:hidden">{action}</div>}<button type="button" onClick={()=>setMobileOpen(false)} className="rounded-full border p-2 text-charcoal-muted xl:hidden" aria-label="Fermer l'aperçu"><X size={18}/></button></div>
        </div>
        {dirty&&<p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[11px] text-amber-800">Modifications non enregistrées, donc non visibles dans l’aperçu.</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1" role="group" aria-label="Format de l'aperçu">{VIEWPORTS.map(item=><button type="button" key={item.id} onClick={()=>setViewportId(item.id)} aria-pressed={viewportId===item.id} title={item.label} className={viewportId===item.id?"inline-flex min-h-10 items-center gap-1 rounded-lg bg-charcoal px-2.5 py-1.5 text-[10px] text-white":"inline-flex min-h-10 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[10px] text-charcoal-muted"}><item.icon size={13}/>{item.label}</button>)}</div>
          <div className="flex items-center gap-2"><button type="button" onClick={refresh} title="Actualiser" aria-label="Actualiser l'aperçu public" className="rounded-lg border p-2 text-charcoal-muted hover:text-sage"><RefreshCw size={15} className={refreshing?"animate-spin":""}/></button><a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-sage hover:underline">Ouvrir en grand <ExternalLink size={12}/></a></div>
        </div>
      </div>
      <div ref={hostRef} className="relative w-full overflow-hidden bg-stone-100" style={{height:`${Math.min(viewport.height*scale,560)}px`}}>
        <iframe ref={iframeRef} key={`${url}-${viewport.id}-${reload}-${refreshKey}`} src={url} onLoad={onLoad} title={`${title}, ${viewport.label}`} className="absolute left-0 top-0 origin-top-left border-0 bg-white" style={{width:`${viewport.width}px`,height:`${viewport.height}px`,transform:`scale(${scale})`}}/>
      </div>
      <p className="border-t border-sage/10 px-3 py-2 text-[10px] text-charcoal-muted">Site réel navigable. Enregistrez le contenu puis actualisez pour voir la version publique.</p>
    </div>
  </aside>;

  return <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(400px,.65fr)]">
    <div className="min-w-0">{action&&<div className="sticky top-6 z-20 mb-[-40px] hidden h-10 items-start justify-end pr-1 pt-1.5 xl:flex">{action}</div>}{action&&<div className="sticky top-3 z-20 mb-3 flex justify-end xl:hidden">{action}</div>}{children}<button type="button" onClick={openMobile} className="btn-secondary mt-5 w-full xl:hidden"><Monitor className="inline" size={16}/> Voir l’aperçu public</button></div>
    {preview}
  </div>;
}
